import { useState } from 'react';
import { Customer, DropLocation, Service } from '~/lib/api/smartsend/types';

import { DropShop } from './DropShop';
import Loading from '../../../../components/loading/Loading';
import { Tagged } from '../Tagged';
import { P2GLink, P2GBox, SvgMapMarkerSearch, P2GText, P2GAlert } from '@parcel2go/core-ui';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { P2GSidePanel } from '~/components/sidepanel';
import { P2GImageMessage } from '~/components/imagemessage';
import { TranslationNamespaces } from '~/lib/constants';
import { Trans, useTranslation } from 'react-i18next';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';

interface props {
  customer: Customer;
  service: Service;
  provider: string;
  type: 'LINK' | 'CTA';
}

const DropShopSearch: React.FC<props> = (props) => {
  const { getDropLocations } = useSmartSendApi();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<DropLocation[]>([]);
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const openModal = () => {
    setLoading(true);
    setOpen(true);
    getDropLocations(
      props.service.Provider,
      props.service.AccountId,
      props.provider,
      props.customer.CountryIsoCode,
      props.customer.Postcode || ''
    ).then((r) => {
      setLocations(r.success ? r.result.DropLocations : []);
      setLoading(false);
    });
  };

  if (!props.provider) {
    return null;
  }

  return (
    <>
      {props.type == 'LINK' && (
        <P2GLink onClick={openModal}>{t('DROP_SHOP_LOCATIONS_LINK', { defaultValue: 'Drop Locations' })}</P2GLink>
      )}
      {props.type === 'CTA' && (
        <P2GBox onClick={openModal}>
          <P2GAlert
            icon={
              <CdnIconProxy size="sm" palette="primary" src="/theme/icons/MapMarkerSearch.svg">
                <SvgMapMarkerSearch />
              </CdnIconProxy>
            }
          >
            <P2GText>
              <Trans t={t} i18nKey="DROP_SHOP_NEARBY_LOCATIONS" defaults="<strong>Nearby drop locations: </strong>" />
            </P2GText>
            <P2GText variant="body2" data-sentry-mask>
              <Trans
                t={t}
                i18nKey="DROP_SHOP_NEARBY_LOCATIONS_DESC"
                values={{ postcode: props.customer.Postcode }}
                defaults="Find locations near <strong>{{ postcode }}</strong>."
              />
            </P2GText>
          </P2GAlert>
        </P2GBox>
      )}
      <P2GSidePanel
        open={open}
        anchor="right"
        onClose={() => setOpen(false)}
        title={t('DROP_SHOP_LOCATION_SEARCH', { defaultValue: 'Location Search' })}
      >
        <Tagged tag="panel-dropshop-search">
          {loading ? (
            <Loading />
          ) : (
            <P2GBox>
              {locations.length === 0 ? (
                <P2GBox>
                  <P2GImageMessage
                    message={`No results for ${props.customer.Postcode}`}
                    image="/theme/common/notifications/no-results.png"
                    contentKey="image-smartsend-dropshop-no-results"
                  />
                </P2GBox>
              ) : (
                <P2GBox display="flex" flexDirection="column" gap={1}>
                  {locations.map((l) => (
                    <DropShop location={l} key={l.Id} />
                  ))}
                </P2GBox>
              )}
            </P2GBox>
          )}
        </Tagged>
      </P2GSidePanel>
    </>
  );
};

export { DropShopSearch };
